import { Component, Host, h, Prop, Event, EventEmitter, Method, State, Listen, Element } from '@stencil/core';
import Swiper from 'swiper';
import { ComponentSize } from '../../interface';
import { hasSlot } from '../../utils';

/**
 * @slot - Modal body.
 * @slot heading - Modal heading slot for html content.
 * @slot footer - Modal footer.
 * @slot primary - Modal primary action button.
 * @slot secondary - Modal secondary action button.
 *
 * @part modal - The component's main modal element.
 * @part panel - The component's panel element.
 * @part header - The component's header element.
 * @part hr - The component's header horizontal rule element.
 * @part footer - The component's footer element.
 */
@Component({
  tag: 'o2-modal',
  styleUrl: 'modal.scss',
  shadow: true,
})
export class O2Modal {
  private hasHeadingSlot: boolean;
  private hasFooterSlot: boolean;
  private hasPrimarySlot: boolean;
  private hasSecondarySlot: boolean;
  private needSwiper: boolean = false;

  private swiperInstance: Swiper;

  @Element() private el: HTMLO2ModalElement;

  @State() open: boolean = false;
  @State() hasScroll: boolean = false;

  /**
   * Main heading of the modal.
   */
  @Prop() heading?: string;

  /**
   * The modal size.
   * Possible values: `"small"`, `"medium"`, `"large"`.
   */
  @Prop() size?: ComponentSize = 'medium';

  /**
   * If true, is it not possible to close modal with click on overlay.
   */
  @Prop() isBlocking?: boolean = false;

  /**
   * Emitted when the modal is open.
   */
  @Event() o2Show!: EventEmitter<any>;

  /**
   * Emitted when the modal is hidden.
   */
  @Event() o2Hide!: EventEmitter<any>;

  /**
   * Show modal manualy.
   */
  @Method()
  async show() {
    if (this.open) {
      return;
    }

    this.open = true;
    this.o2Show.emit();

    this.lockPageScroll();

    if (this.needSwiper) {
      await this.importSwiperAndRegisterEvents();
    }

    this.hasModalBodyScroll();
  }

  /**
   * Hide modal manualy.
   */
  @Method()
  async hide(initiator?: string) {
    if (!this.open) {
      return;
    }

    this.unlockPageScroll();

    this.open = false;
    this.o2Hide.emit({ initiator: initiator });
  }

  @Listen('keydown', { target: 'document' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.hide('escape');
    }
  }

  private lockPageScroll(): void {
    document.body.classList.add('o2-scroll-lock');
  }

  private unlockPageScroll(): void {
    document.body.classList.remove('o2-scroll-lock');
  }

  private onClickCloseOverlay = () => {
    if (!this.isBlocking) {
      this.hide('overlay');
    }
  };

  private hasModalBodyScroll(): void {
    const modalBody = this.el.shadowRoot.querySelector('.modal__body');

    if (modalBody?.clientHeight < modalBody?.scrollHeight) {
      this.hasScroll = true;
    } else {
      this.hasScroll = false;
    }
  }

  private hasMobileResolution(): boolean {
    const deviceWidth = window.innerWidth;
    return deviceWidth < 576 ? true : false;
  }

  private importSwiperAndRegisterEvents = async () => {
    const { Swiper } = await import('swiper');

    const modalPanel: HTMLElement = this.el.shadowRoot.querySelector('.swiper');

    this.swiperInstance = new Swiper(modalPanel, {
      direction: 'vertical',
      slidesPerView: 'auto',
      centeredSlides: true,
    });

    this.swiperInstance.on('touchMove', event => {
      this.swiperInstance.allowSlideNext = false;

      if (event.touches.diff > 200) {
        this.hide();
      }
    });
  };

  /** Get real 100vh on every device. BUG-41780
   * https://css-tricks.com/the-trick-to-viewport-units-on-mobile
   */
  private getVHUnitSize = () => {
    let vh = window.innerHeight * 0.01;
    this.el.style.setProperty('--vh', `${vh}px`);
  };

  /**
   * Update swiper state if some external change of slides happened.
   */
  private updateSwiper = () => {
    if (this.swiperInstance) {
      this.swiperInstance.update();
    }
  };

  /**
   * ResizeObserver is checking changes on elements, also clientHeight.
   */
  private observeHeightChange = () => {
    const modalPanel = this.el.shadowRoot.querySelector('.modal__panel');
    const resizeObserver = new ResizeObserver(() => {
      this.hasModalBodyScroll();
      this.updateSwiper();
    });
    resizeObserver.observe(modalPanel);
  };

  connectedCallback() {
    this.hasHeadingSlot = hasSlot(this.el, '[slot="heading"]');
    this.hasFooterSlot = hasSlot(this.el, '[slot="footer"]');
    this.hasPrimarySlot = hasSlot(this.el, '[slot="primary"]');
    this.hasSecondarySlot = hasSlot(this.el, '[slot="secondary"]');
    this.getVHUnitSize();

    this.needSwiper = this.size === 'small' && this.hasMobileResolution();
  }

  componentDidLoad() {
    const secondaryButton = this.el.querySelector('o2-button[slot="secondary"]');

    if (secondaryButton) {
      secondaryButton.addEventListener('click', event => {
        if (!event.defaultPrevented) {
          this.hide('button');
        }
      });
    }

    this.observeHeightChange();
  }

  render() {
    const close = !this.isBlocking ? <o2-icon class="modal__close" name="close-circle" solid color="gray-medium" size={24} onClick={() => this.hide('close')}></o2-icon> : '';

    return (
      <Host>
        <div
          part="modal"
          class={`${this.needSwiper ? 'swiper' : ''} modal ${this.open ? 'modal--open' : ''} ${this.open ? 'modal--popin' : 'modal--popout'} ${
            this.size ? `modal--${this.size}` : ''
          } ${close !== '' ? 'modal--has-close' : ''}`}
          role="modal"
        >
          <div class="modal__overlay" onClick={this.onClickCloseOverlay}></div>
          <div class={`${this.needSwiper ? 'swiper-wrapper' : 'wrapper'}`}>
            <div part="panel" class={`modal__panel ${this.needSwiper ? 'swiper-slide' : ''}`}>
              {this.needSwiper ? <div class="modal__pull"></div> : ''}
              {this.hasHeadingSlot || this.heading ? (
                <header part="header" class="modal__header">
                  <h2>
                    {this.heading ? this.heading : ''}
                    {this.hasHeadingSlot ? <slot name="heading"></slot> : ''}
                  </h2>
                  <hr part="hr" />
                </header>
              ) : (
                ''
              )}

              {close}

              <section part="body" class="modal__body">
                <slot></slot>
              </section>
              {this.hasFooterSlot || this.hasPrimarySlot || this.hasSecondarySlot ? (
                <footer part="footer" class={`modal__footer ${this.hasScroll ? 'modal__footer--shadow' : ''}`}>
                  {this.hasSecondarySlot ? <slot name="secondary"></slot> : ''}
                  {this.hasPrimarySlot ? <slot name="primary"></slot> : ''}
                  {this.hasFooterSlot ? <slot name="footer"></slot> : ''}
                </footer>
              ) : (
                ''
              )}
            </div>

            {this.needSwiper ? <div class="swiper-slide swiper-slide--fake"></div> : ''}
          </div>
        </div>
      </Host>
    );
  }
}
